<template>
  <div class="recent-activity-container">
    <div class="activity-header">
      <div class="header-title">
        <i class="fa-solid fa-clock-rotate-left"></i>
        <h3>Recent Activity</h3>
      </div>
    </div>
    <div class="activity-content">
      <div class="activity-grid">
        <div class="activity-row header">
          <div class="col">Student</div>
          <div class="col">Clock In</div>
          <div class="col">Clock Out</div>
          <div class="col">Duration</div>
          <div class="col">Status</div>
        </div>
        <div v-for="(log, index) in paginatedLogs" :key="index" 
             class="activity-row" 
             :class="{ 'even': index % 2 === 0 }">
          <div class="col student-col">
            <div class="avatar" :style="{ backgroundColor: getAvatarColor(log.studentName) }">
              {{ getInitials(log.studentName) }}
            </div>
            <div class="student-info">
              <span class="name">{{ log.studentName }}</span>
            </div>
          </div>
          <div class="col">
            <i class="fa-solid fa-sign-in-alt clock-in-icon"></i>
            {{ formatDateTime(log.clockIn) }}
          </div>
          <div class="col">
            <i class="fa-solid fa-sign-out-alt clock-out-icon"></i>
            {{ formatDateTime(log.clockOut) }}
          </div>
          <div class="col">{{ calculateDuration(log.clockIn, log.clockOut) }}</div>
          <div class="col">
            <span class="status-badge" :class="{ 'checked-out': log.clockOut }">
              {{ log.clockOut ? 'Checked Out' : 'Active' }}
            </span>
          </div>
        </div>
      </div>
      <div v-if="!logs.length" class="no-activity">
        <i class="fa-solid fa-clock"></i>
        <p>No recent activity</p>
      </div>
      
      <!-- Pagination -->
      <div v-if="logs.length > itemsPerPage" class="pagination">
        <button 
          class="pagination-btn" 
          :disabled="currentPage === 1"
          @click="currentPage--"
          aria-label="Previous page"
        >
          <i class="fas fa-angle-left"></i>
        </button>
        
        <div class="page-info">
          <span>Page {{ currentPage }} of {{ totalPages }}</span>
          <span class="page-details">Showing {{ startIndex + 1 }}-{{ endIndex }} of {{ logs.length }}</span>
        </div>

        <button 
          class="pagination-btn" 
          :disabled="currentPage === totalPages"
          @click="currentPage++"
          aria-label="Next page"
        >
          <i class="fas fa-angle-right"></i>
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import moment from 'moment';

export default {
  name: 'RecentActivity',
  props: {
    logs: {
      type: Array,
      default: () => []
    }
  },
  data() {
    return {
      currentPage: 1,
      itemsPerPage: 10
    }
  },
  computed: {
    totalPages() {
      return Math.ceil(this.logs.length / this.itemsPerPage);
    },
    startIndex() {
      return (this.currentPage - 1) * this.itemsPerPage;
    },
    endIndex() {
      return Math.min(this.startIndex + this.itemsPerPage, this.logs.length);
    },
    paginatedLogs() {
      return this.logs.slice(this.startIndex, this.endIndex);
    }
  },
  watch: {
    logs() {
      // Reset to first page when logs change
      this.currentPage = 1;
    }
  },
  methods: {
    getInitials(name) {
      return name
        .split(' ')
        .map(word => word[0])
        .join('')
        .toUpperCase()
        .slice(0, 2);
    },
    formatDateTime(dateTime) {
      if (!dateTime) return '---';
      return moment(dateTime).format('hh:mm A');
    },
    calculateDuration(start, end) {
      if (!start || !end) return '---';
      const duration = moment.duration(moment(end).diff(moment(start)));
      const hours = Math.floor(duration.asHours());
      const minutes = Math.floor(duration.asMinutes() % 60);
      return `${hours}h ${minutes.toString().padStart(2, '0')}m`;
    },
    getAvatarColor(name) {
      const colors = [
        '#10B981', '#3B82F6', '#6366F1', '#8B5CF6', 
        '#EC4899', '#F43F5E', '#F97316', '#EAB308'
      ];
      let hash = 0;
      for (let i = 0; i < name.length; i++) {
        hash = name.charCodeAt(i) + ((hash << 5) - hash);
      }
      return colors[Math.abs(hash) % colors.length];
    }
  }
}
</script>

<style scoped>
.recent-activity-container {
  background: white;
  border-radius: 16px;
  box-shadow: 0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1);
  width: 100%;
  overflow: hidden;
}

.activity-header {
  padding: 1.5rem;
  border-bottom: 1px solid #e2e8f0;
}

.header-title {
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.header-title i {
  font-size: 1.5rem;
  color: #64748b;
}

.header-title h3 {
  font-size: 1.5rem;
  font-weight: 600;
  color: #1e293b;
  margin: 0;
}

.activity-content {
  padding: 1rem;
}

.activity-grid {
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  overflow: hidden;
}

.activity-row {
  display: grid;
  grid-template-columns: 2fr 1.2fr 1.2fr 1fr 1fr;
  align-items: center;
  padding: 1rem 1.5rem;
  transition: background-color 0.2s ease;
}

.activity-row.header {
  background: #f8fafc;
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: #64748b;
  padding: 0.75rem 1.5rem;
}

.activity-row.even {
  background: #f8fafc;
}

.activity-row:not(.header):hover {
  background: #f1f5f9;
}

.col {
  padding: 0.5rem;
  font-size: 0.875rem;
  color: #475569;
}

.student-col {
  display: flex;
  align-items: center;
  gap: 1rem;
}

.avatar {
  width: 2.5rem;
  height: 2.5rem;
  border-radius: 9999px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.875rem;
  font-weight: 600;
  color: white;
}

.student-info {
  display: flex;
  flex-direction: column;
}

.name {
  font-weight: 500;
  color: #1e293b;
  font-size: 0.875rem;
}

.clock-in-icon {
  color: #10b981;
  margin-right: 0.5rem;
}

.clock-out-icon {
  color: #f97316;
  margin-right: 0.5rem;
}

.status-badge {
  display: inline-flex;
  align-items: center;
  padding: 0.25rem 0.75rem;
  border-radius: 9999px;
  font-size: 0.75rem;
  font-weight: 500;
  background: #dcfce7;
  color: #15803d;
}

.status-badge.checked-out {
  background: #f1f5f9;
  color: #64748b;
}

.no-activity {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 4rem 1rem;
  color: #64748b;
}

.no-activity i {
  font-size: 2.5rem;
  margin-bottom: 1rem;
  opacity: 0.5;
}

.no-activity p {
  font-size: 1rem;
  font-weight: 500;
}

.pagination {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 1rem;
  margin-top: 1.5rem;
  padding: 1rem;
  border-top: 1px solid #e2e8f0;
}

.pagination-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 2.5rem;
  height: 2.5rem;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  background: white;
  color: #64748b;
  cursor: pointer;
  transition: all 0.2s ease;
}

.pagination-btn:hover:not(:disabled) {
  background: #f8fafc;
  color: #1e293b;
  border-color: #cbd5e1;
}

.pagination-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.page-info {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.25rem;
}

.page-info span {
  color: #64748b;
  font-size: 0.875rem;
}

.page-details {
  font-size: 0.75rem;
  color: #94a3b8;
}

.pagination-btn i {
  font-size: 1.2rem;
}

@media (max-width: 1024px) {
  .activity-row {
    grid-template-columns: 2fr 1fr 1fr 1fr 1fr;
    padding: 0.75rem 1rem;
  }

  .avatar {
    width: 2rem;
    height: 2rem;
  }
}

@media (max-width: 768px) {
  .activity-row.header {
    display: none;
  }

  .activity-row {
    grid-template-columns: 1fr;
    gap: 0.5rem;
    padding: 1rem;
    border-bottom: 1px solid #e2e8f0;
  }

  .col {
    padding: 0.25rem;
  }

  .col:not(.student-col) {
    padding-left: 3.5rem;
  }

  .activity-row:last-child {
    border-bottom: none;
  }

  .pagination {
    flex-wrap: wrap;
    gap: 0.5rem;
  }

  .page-info {
    order: -1;
    width: 100%;
    margin-bottom: 0.5rem;
  }
}
</style> 